import {
    calculatePercentage,
    formatPercent,
    oversizedUndersizedState,
    formatStorageSize,
    calcReadyForMigration,
    deriveNetTrend,
    deriveRowTrend,
} from '../../SummaryWrapper/inventorySummaryHelpers';
import { formatCount, formatDecimal } from '../../../lib/helpers';
import { getGiBfromMiB } from '../../../lib/helpers';

export const parseReadinessResponse = ({ summaryData, totalInventory }) => {
    const readyForMigration = calcReadyForMigration(summaryData?.ready);
    const rawPercent = totalInventory > 0 ? (readyForMigration / totalInventory) * 100 : 0;
    const percentReady = rawPercent > 0 && rawPercent < 1 ? 1 : Math.floor(rawPercent);
    return {
        totalOversizedVMs: summaryData?.oversized,
        totalUndersizedVMs: summaryData?.undersized,
        readyForMigration,
        percentReady,
        totalInventory,
    };
};

export const readinessCardValues = (
    { totalOversizedVMs, totalUndersizedVMs, readyForMigration, percentReady, totalInventory },
    handlers,
) => ({
    title: 'Readiness',
    subLabelIcon: 'migrate',
    subLabelIconClass: 'success-icon',
    subLabel: 'Ready for Migration',
    primaryValue: formatCount(readyForMigration),
    primaryPercent: `${percentReady}%`,
    description: 'Instances meeting\nall thresholds',
    onPrimaryClick: handlers?.onFilterClick ? () => handlers.onFilterClick(['ready']) : undefined,
    rows: [
        {
            variant: 'ready',
            label: 'Total VMs',
            count: formatCount(totalInventory),
            icon: 'vm',
            onClick: handlers?.onFilterClick ? () => handlers.onFilterClick([]) : undefined,
        },
        {
            variant: 'warning',
            label: 'Oversized VMs',
            count: formatCount(totalOversizedVMs),
            percent: formatPercent(calculatePercentage(totalOversizedVMs, totalInventory)),
            iconVariant: 'notReady',
            icon: 'oversized',
            onClick: handlers?.onFilterClick
                ? () => handlers.onFilterClick(['oversized_cpu', 'oversized_ram'])
                : undefined,
        },
        {
            variant: 'warning',
            label: 'Undersized VMs',
            count: formatCount(totalUndersizedVMs),
            percent: formatPercent(calculatePercentage(totalUndersizedVMs, totalInventory)),
            icon: 'undersized',
            onClick: handlers?.onFilterClick
                ? () => handlers.onFilterClick(['undersized_cpu', 'undersized_ram'])
                : undefined,
        },
    ],
});

export const parseCPUResponse = ({ summaryData, totalInventory }) => {
    const undersizedCPU = summaryData?.undersized_by_cpu ?? 0;
    const cpuReductionOpportunities = summaryData?.oversized_by_cpu ?? 0;
    const cpuOversizedTrend = summaryData?.cpu_oversized_trend ?? 0;
    const cpuUndersizedTrend = summaryData?.cpu_undersized_trend ?? 0;
    return {
        totalCPU: summaryData?.total_cpu ?? 0,
        undersizedCPU,
        cpuReductionOpportunities,
        optimizedByCPU: Math.max(0, totalInventory - cpuReductionOpportunities - undersizedCPU),
        primaryTrend: deriveNetTrend(cpuOversizedTrend, cpuUndersizedTrend),
        oversizedTrend: deriveRowTrend(cpuOversizedTrend),
        undersizedTrend: deriveRowTrend(cpuUndersizedTrend),
        totalInventory,
    };
};

export const cpuCardValues = (
    {
        totalCPU,
        undersizedCPU,
        cpuReductionOpportunities,
        optimizedByCPU,
        primaryTrend,
        oversizedTrend,
        undersizedTrend,
        totalInventory,
    },
    handlers,
) => ({
    title: 'CPU Utilization',
    onMoreClick: handlers?.onIgnoreOptimizations
        ? () => handlers.onIgnoreOptimizations({ cpu: ['oversized', 'undersized'] })
        : undefined,
    subLabelIcon: 'check',
    subLabelIconClass: 'success-icon',
    subLabel: 'Optimized VMs',
    primaryValue: formatCount(optimizedByCPU),
    primaryPercent: formatPercent(calculatePercentage(optimizedByCPU, totalInventory)),
    primaryTrend,
    description: 'Instances meeting\nperformance thresholds',
    rows: [
        {
            variant: 'ready',
            label: 'Total Core Count',
            count: formatCount(totalCPU),
            countSuffix: 'vCPUs',
            icon: 'cpu',
        },
        {
            variant: 'ready',
            label: 'Oversized VMs',
            count: formatCount(cpuReductionOpportunities),
            percent: formatPercent(calculatePercentage(cpuReductionOpportunities, totalInventory)),
            percentTrend: oversizedTrend,
            oversizedUndersizedState: oversizedUndersizedState(oversizedTrend),
            iconVariant: 'notReady',
            icon: 'oversized',
            onClick: handlers?.onFilterClick ? () => handlers.onFilterClick(['oversized_cpu']) : undefined,
        },
        {
            variant: 'notReady',
            label: 'Undersized VMs',
            count: formatCount(undersizedCPU),
            percent: formatPercent(calculatePercentage(undersizedCPU, totalInventory)),
            percentTrend: undersizedTrend,
            oversizedUndersizedState: oversizedUndersizedState(undersizedTrend),
            icon: 'undersized',
            onClick: handlers?.onFilterClick ? () => handlers.onFilterClick(['undersized_cpu']) : undefined,
        },
    ],
});

export const parseRAMResponse = ({ summaryData, totalInventory }) => {
    const undersizedRAM = summaryData?.undersized_by_ram ?? 0;
    const ramReductionOpportunities = summaryData?.oversized_by_ram ?? 0;
    const ramOversizedTrend = summaryData?.ram_oversized_trend ?? 0;
    const ramUndersizedTrend = summaryData?.ram_undersized_trend ?? 0;
    return {
        totalRAM: summaryData?.total_ram
            ? formatStorageSize(Math.floor(getGiBfromMiB(summaryData.total_ram)))
            : { value: 0, unit: 'GiB' },
        undersizedRAM,
        ramReductionOpportunities,
        optimizedByRAM: Math.max(0, totalInventory - ramReductionOpportunities - undersizedRAM),
        primaryTrend: deriveNetTrend(ramOversizedTrend, ramUndersizedTrend),
        oversizedTrend: deriveRowTrend(ramOversizedTrend),
        undersizedTrend: deriveRowTrend(ramUndersizedTrend),
        totalInventory,
    };
};

export const ramCardValues = (
    {
        totalRAM,
        undersizedRAM,
        ramReductionOpportunities,
        optimizedByRAM,
        primaryTrend,
        oversizedTrend,
        undersizedTrend,
        totalInventory,
    },
    handlers,
) => ({
    title: 'RAM Utilization',
    onMoreClick: handlers?.onIgnoreOptimizations
        ? () => handlers.onIgnoreOptimizations({ ram: ['oversized', 'undersized'] })
        : undefined,
    subLabelIcon: 'check',
    subLabelIconClass: 'success-icon',
    subLabel: 'Optimized VMs',
    primaryValue: formatCount(optimizedByRAM),
    primaryPercent: formatPercent(calculatePercentage(optimizedByRAM, totalInventory)),
    primaryTrend,
    description: 'Instances meeting\nutilization thresholds',
    rows: [
        {
            variant: 'ready',
            label: 'Total RAM',
            count: formatDecimal(totalRAM.value),
            countSuffix: totalRAM.unit,
            icon: 'ram',
        },
        {
            variant: 'ready',
            label: 'Oversized VMs',
            count: formatCount(ramReductionOpportunities),
            percent: formatPercent(calculatePercentage(ramReductionOpportunities, totalInventory)),
            percentTrend: oversizedTrend,
            oversizedUndersizedState: oversizedUndersizedState(oversizedTrend),
            iconVariant: 'notReady',
            icon: 'oversized',
            onClick: handlers?.onFilterClick ? () => handlers.onFilterClick(['oversized_ram']) : undefined,
        },
        {
            variant: 'notReady',
            label: 'Undersized VMs',
            count: formatCount(undersizedRAM),
            percent: formatPercent(calculatePercentage(undersizedRAM, totalInventory)),
            percentTrend: undersizedTrend,
            oversizedUndersizedState: oversizedUndersizedState(undersizedTrend),
            icon: 'undersized',
            onClick: handlers?.onFilterClick ? () => handlers.onFilterClick(['undersized_ram']) : undefined,
        },
    ],
});

export const parseStorageResponse = ({ summaryData, totalInventory }) => {
    const undersizedStorage = summaryData?.undersized_by_storage ?? 0;
    const storageReductionOpportunities = summaryData?.oversized_by_storage ?? 0;
    const storageOversizedTrend = summaryData?.storage_oversized_trend ?? 0;
    const storageUndersizedTrend = summaryData?.storage_undersized_trend ?? 0;
    return {
        totalStorage: summaryData?.total_storage
            ? formatStorageSize(Math.floor(getGiBfromMiB(summaryData.total_storage)))
            : { value: 0, unit: 'GiB' },
        undersizedStorage,
        storageReductionOpportunities,
        optimizedByStorage: Math.max(0, totalInventory - storageReductionOpportunities - undersizedStorage),
        primaryTrend: deriveNetTrend(storageOversizedTrend, storageUndersizedTrend),
        oversizedTrend: deriveRowTrend(storageOversizedTrend),
        undersizedTrend: deriveRowTrend(storageUndersizedTrend),
        totalInventory,
    };
};

export const storageCardValues = (
    {
        totalStorage,
        undersizedStorage,
        storageReductionOpportunities,
        optimizedByStorage,
        primaryTrend,
        oversizedTrend,
        undersizedTrend,
        totalInventory,
    },
    handlers,
) => ({
    title: 'Storage Utilization',
    onMoreClick: handlers?.onIgnoreOptimizations
        ? () => handlers.onIgnoreOptimizations({ storage: ['oversized', 'undersized'] })
        : undefined,
    subLabelIcon: 'check',
    subLabelIconClass: 'success-icon',
    subLabel: 'Optimized VMs',
    primaryValue: formatCount(optimizedByStorage),
    primaryPercent: formatPercent(calculatePercentage(optimizedByStorage, totalInventory)),
    primaryTrend,
    description: 'Instances meeting\nutilization thresholds',
    rows: [
        {
            variant: 'ready',
            label: 'Total Storage',
            count: formatDecimal(totalStorage.value),
            countSuffix: totalStorage.unit,
            icon: 'storage',
        },
        {
            variant: 'ready',
            label: 'Oversized VMs',
            count: formatCount(storageReductionOpportunities),
            percent: formatPercent(calculatePercentage(storageReductionOpportunities, totalInventory)),
            percentTrend: oversizedTrend,
            oversizedUndersizedState: oversizedUndersizedState(oversizedTrend),
            iconVariant: 'notReady',
            icon: 'oversized',
        },
        {
            variant: 'notReady',
            label: 'Undersized VMs',
            count: formatCount(undersizedStorage),
            percent: formatPercent(calculatePercentage(undersizedStorage, totalInventory)),
            percentTrend: undersizedTrend,
            oversizedUndersizedState: oversizedUndersizedState(undersizedTrend),
            icon: 'undersized',
        },
    ],
});
